import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const realityData = [
  { value: 76, title: { de: 'Stress im Frontline-Betrieb', en: 'Frontline Stress' }, label: { de: 'der Frontline-Mitarbeitenden in Deutschland erleben regelmäßig arbeitsbedingten Stress – KI kann gezielt entlasten.', en: 'of frontline employees in Germany regularly experience work-related stress – AI can provide targeted relief.' }, source: 'Quinyx, State of the Frontline Workforce 2024' },
  { value: 77, title: { de: 'Technologie wird nicht genutzt', en: 'Technology Goes Unused' }, label: { de: 'der Teams sagen: Technologie ist vorhanden, aber falsch eingeführt, kaum nutzbar oder im Alltag nicht angekommen.', en: 'of teams say: technology is available but poorly implemented, barely usable, or has not landed in daily operations.' }, source: 'SD Worx / Quinyx, State of the Frontline Workforce 2024' },
  { value: 65, title: { de: 'Teams sind offen für KI', en: 'Teams Open to AI' }, label: { de: 'der Frontline Worker erwarten, dass sich die Qualität ihrer Arbeit durch moderne Technologie spürbar verbessert.', en: 'of frontline workers expect the quality of their work to noticeably improve through modern technology.' }, source: 'SD Worx / Quinyx, State of the Frontline Workforce 2024' },
  { value: 85, title: { de: 'KI gilt als strategisch zentral', en: 'AI Seen as Strategically Central' }, label: { de: 'der Handelsunternehmen in DACH sehen KI als entscheidend für ihre Zukunftsfähigkeit – aber kaum einer setzt sie operativ um.', en: 'of retail companies in DACH see AI as crucial for their future viability – but hardly anyone implements it operationally.' }, source: 'VoCoVo-Studie DACH 2025/2026' },
  { value: 61, title: { de: 'Messbare Effizienzgewinne', en: 'Measurable Efficiency Gains' }, label: { de: 'der Unternehmen berichten von nachweisbaren Effizienzgewinnen durch gezielten KI-Einsatz im Retail-Betrieb.', en: 'of companies report measurable efficiency gains from targeted AI deployment in retail operations.' }, source: 'KPMG / Intelligent Retail' },
  { value: 65, title: { de: 'ROI innerhalb von 12 Monaten', en: 'ROI Within 12 Months' }, label: { de: 'der Unternehmen erwarten einen positiven Return on Investment auf ihre KI-Investitionen innerhalb eines Jahres.', en: 'of companies expect a positive return on investment from their AI investments within one year.' }, source: 'KPMG / Intelligent Retail' },
];

const ORANGE = '#e07a2f';

function DonutCounter({ value, size = 110, strokeWidth = 7 }) {
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPlayed(true);
        const duration = 1800;
        const start = performance.now();
        const animate = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          setProgress(Math.round(eased * value));
          if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.2 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, played]);

  const offset = circumference - (progress / 100) * circumference;

  return (
    <div ref={ref} className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e5e5" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={ORANGE} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Oswald', sans-serif", color: ORANGE }}>
          {progress}<span className="text-base">%</span>
        </span>
      </div>
    </div>
  );
}

export default function RealityCheckSection() {
  const { lang } = useLanguage();

  return (
    <section id="reality-check" data-testid="reality-check-section" className="bg-[#fafafa] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-xs uppercase tracking-[0.3em] mb-4 font-bold"
          style={{ fontFamily: "'Oswald', sans-serif", color: ORANGE }}
        >
          {lang === 'de' ? 'Studien & Zahlen' : 'Studies & Numbers'}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] mb-4"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          {lang === 'de' ? 'Was Studien über KI und Frontline-Teams im Retail sagen.' : 'What studies say about AI and frontline teams in retail.'}
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {realityData.map((item, i) => (
            <motion.div key={i} data-testid={`reality-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="border border-[#e5e5e5] p-6 flex items-start gap-5 hover:border-[#e07a2f]/40 transition-colors duration-300"
            >
              <DonutCounter value={item.value} />
              <div className="flex-1 min-w-0">
                <p className="text-[#1f1f1f] text-sm font-bold uppercase tracking-wide mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.title[lang]}
                </p>
                <p className="text-[#1f1f1f]/70 text-sm leading-relaxed mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.label[lang]}
                </p>
                <p className="text-[#1f1f1f]/30 text-[10px] italic" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.source}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
